import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InventorySlideOver, slideOverCancelClass } from '../../ui/InventorySlideOver';
import { cn } from '../../../lib/utils';
import { borderPrimary, cardBg, greenButtonClass, greenTextColor, primaryText, redTextColor, secondaryText } from '../../../styling/stylingHelper';
import { currencyFormatter } from '../../../services/helperService';
import {
  AdminAmbassadorReferral, AmbassadorReferral, createReferral, deleteAdminReferral, ReferralApiError,
  ReferralDetails, ReferralStatus, REFERRAL_STATUSES, updateAdminReferral, updateReferral,
} from '../../../services/ambassadorReferrals/ambassadorReferralsService';

export const referralInputClass = cn('w-full min-w-0 px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-400 disabled:opacity-70', borderPrimary, cardBg, primaryText);

interface Props {
  lead: AmbassadorReferral | null;
  admin: boolean;
  onClose: () => void;
  onSaved: () => void;
  onReload: () => void;
}

export default function ReferralSidebar({ lead, admin, onClose, onSaved, onReload }: Props) {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ReferralApiError | null>(null);
  const [confirmCorrection, setConfirmCorrection] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [businessName, setBusinessName] = useState(lead?.businessName ?? '');
  const [restaurantCount, setRestaurantCount] = useState(String(lead?.restaurantCount ?? 1));
  const [contactName, setContactName] = useState(lead?.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(lead?.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(lead?.contactPhone ?? '');
  const [notes, setNotes] = useState(lead?.notes ?? '');
  const [status, setStatus] = useState<ReferralStatus>(lead?.status ?? 'pending');
  const [convertedCount, setConvertedCount] = useState(String(lead?.convertedRestaurantCount ?? lead?.restaurantCount ?? 1));
  const [price, setPrice] = useState(String(admin && lead ? (lead as AdminAmbassadorReferral).monthlyPricePerRestaurantCents / 100 : 300));
  const [startDate, setStartDate] = useState(lead?.commissionStartDate ?? '');
  const [churnedOn, setChurnedOn] = useState(lead?.churnedOn ?? '');
  const editable = admin || !lead || lead.status === 'pending' || lead.status === 'new';
  const converted = status === 'won' || status === 'churned';
  const wasConverted = lead?.status === 'won' || lead?.status === 'churned';
  const needsConfirmation = admin && wasConverted && !converted;
  // Date inputs represent Amsterdam business dates, independent of the browser timezone.
  const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const previewCents = Math.round(Number(convertedCount) * Math.round(Number(price) * 100) * 15 / 100);

  useEffect(() => {
    mounted.current = true;
    const previous = document.activeElement as HTMLElement | null;
    const first = container.current?.querySelector<HTMLElement>('input:not(:disabled), button');
    first?.focus();
    return () => { mounted.current = false; previous?.focus(); };
  }, []);

  const close = () => { if (!saving) onClose(); };
  const fieldError = (name: string) => error?.fields.includes(name);
  const field = (name: string, control: ReactNode, hint?: string) => (
    <div className="space-y-1.5">
      <label htmlFor={`referral-${name}`} className={cn('block text-sm font-medium', primaryText)}>{t(`ambassador.fields.${name}`)}</label>
      {control}
      {hint && <p className={cn('text-xs', secondaryText)}>{hint}</p>}
      {fieldError(name) && <p id={`referral-${name}-error`} className={cn('text-xs', redTextColor)} role="alert">{t(`ambassador.errors.${error?.code === 'duplicate_lead' ? 'duplicateField' : 'invalidField'}`)}</p>}
    </div>
  );
  const attributes = (name: string) => ({
    id: `referral-${name}`,
    className: referralInputClass,
    disabled: !editable || saving,
    'aria-invalid': !!fieldError(name),
    'aria-describedby': fieldError(name) ? `referral-${name}-error` : undefined,
  });

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!editable || saving || confirmDelete) return;
    setError(null);
    if (!lead && !contactName.trim()) {
      setError(new ReferralApiError('invalid_input', ['contactName']));
      return;
    }
    if (!contactEmail.trim() && !contactPhone.trim()) {
      setError(new ReferralApiError('contact_required', ['contactEmail', 'contactPhone']));
      return;
    }
    if (needsConfirmation && !confirmCorrection) return;
    const details: ReferralDetails = {
      businessName: businessName.trim(), restaurantCount: Number(restaurantCount),
      contactName: contactName.trim() || null, contactEmail: contactEmail.trim() || null,
      contactPhone: contactPhone.trim() || null, notes: notes.trim() || null,
    };
    setSaving(true);
    try {
      if (admin && lead) {
        await updateAdminReferral(lead.id, {
          ...details, updatedAt: lead.updatedAt, status,
          convertedRestaurantCount: converted ? Number(convertedCount) : null,
          monthlyPricePerRestaurantCents: converted ? Math.round(Number(price) * 100) : 30000,
          commissionStartDate: converted ? startDate : null,
          churnedOn: status === 'churned' ? churnedOn : null,
        });
      } else if (lead) {
        await updateReferral(lead.id, { ...details, updatedAt: lead.updatedAt });
      } else {
        await createReferral(details);
      }
      if (mounted.current) onSaved();
    } catch (err) {
      console.error('Failed to save ambassador referral', err);
      if (mounted.current) setError(err instanceof ReferralApiError ? err : new ReferralApiError('internal_error'));
    } finally {
      if (mounted.current) setSaving(false);
    }
  }

  async function remove() {
    if (!admin || !lead || !confirmDelete || saving) return;
    setError(null);
    setSaving(true);
    try {
      await deleteAdminReferral(lead.id, lead.updatedAt);
      if (mounted.current) onSaved();
    } catch (err) {
      console.error('Failed to delete ambassador referral', err);
      if (mounted.current) setError(err instanceof ReferralApiError ? err : new ReferralApiError('internal_error'));
    } finally {
      if (mounted.current) setSaving(false);
    }
  }

  return (
    <div ref={container} role="dialog" aria-modal="true" aria-label={t(lead ? 'ambassador.leadDetails' : 'ambassador.addLead')} onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      if (event.key !== 'Tab') return;
      const items = Array.from(container.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') ?? []);
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }}>
      <InventorySlideOver isOpen onClose={close} closeLabel={t('ambassador.close')} title={t(lead ? 'ambassador.leadDetails' : 'ambassador.addLead')}
        description={t(editable ? 'ambassador.formDescription' : 'ambassador.readOnly')}
        footer={<>
          <button type="button" onClick={close} disabled={saving} className={slideOverCancelClass}>{t('ambassador.close')}</button>
          {editable && <button type="submit" form="ambassador-lead-form" disabled={saving || confirmDelete || (needsConfirmation && !confirmCorrection) || error?.code === 'stale_lead'} className={cn(greenButtonClass, 'flex-1 justify-center disabled:opacity-50')}>{t(saving ? 'ambassador.saving' : 'ambassador.save')}</button>}
        </>}>
        <form id="ambassador-lead-form" onSubmit={save} className="space-y-5 pb-2">
          {error && <div role="alert" className={cn('p-3 rounded-lg border text-sm', borderPrimary, redTextColor)}>
            {t(`ambassador.errors.${['duplicate_lead', 'invalid_input', 'stale_lead', 'forbidden', 'not_found', 'contact_required'].includes(error.code) ? error.code : 'internal_error'}`)}
            {(error.code === 'stale_lead' || error.code === 'not_found') && <button type="button" onClick={onReload} className="block mt-2 underline font-semibold">{t('ambassador.reload')}</button>}
          </div>}
          {lead && <div className={cn('rounded-xl border p-4 space-y-2', borderPrimary)}>
            <p className={cn('font-semibold', primaryText)}>{t(`ambassador.status.${lead.status}`)}</p>
            {lead.status === 'pending' ? <p className={cn('text-sm', secondaryText)}>{t('ambassador.pendingHint')}</p> : <>
            <p className={cn('text-sm', secondaryText)}>{t('ambassador.accumulated')}: <strong className={greenTextColor}>{currencyFormatter.format(lead.accumulatedCommissionCents / 100)}</strong></p>
            <p className={cn('text-sm', secondaryText)}>{t(lead.status === 'new' || lead.status === 'in_progress' ? 'ambassador.potential' : 'ambassador.monthly')}: {currencyFormatter.format((lead.potentialMonthlyCommissionCents || lead.monthlyCommissionCents) / 100)}</p>
            </>}
            {!admin && lead.commissionStartDate && <p className={cn('text-sm', secondaryText)}>{t('ambassador.fields.commissionStartDate')}: {lead.commissionStartDate}</p>}
            {!admin && lead.churnedOn && <p className={cn('text-sm', secondaryText)}>{t('ambassador.fields.churnedOn')}: {lead.churnedOn}</p>}
          </div>}
          {!lead && <p className={cn('text-sm', secondaryText)}>{t('ambassador.pendingHint')}</p>}
          {field('businessName', <input {...attributes('businessName')} value={businessName} onChange={e => setBusinessName(e.target.value)} required maxLength={200} />)}
          {field('restaurantCount', <input {...attributes('restaurantCount')} type="number" min={1} max={10000} step={1} required value={restaurantCount} onChange={e => setRestaurantCount(e.target.value)} />)}
          {field('contactName', <input {...attributes('contactName')} required={!lead} value={contactName} maxLength={200} onChange={e => setContactName(e.target.value)} />)}
          <p className={cn('text-sm', secondaryText)}>{t('ambassador.contactRequirement')}</p>
          {field('contactEmail', <input {...attributes('contactEmail')} type="email" required={!contactPhone.trim()} maxLength={254} value={contactEmail} onChange={e => setContactEmail(e.target.value)} />)}
          {field('contactPhone', <input {...attributes('contactPhone')} type="tel" required={!contactEmail.trim()} maxLength={60} value={contactPhone} onChange={e => setContactPhone(e.target.value)} />, t('ambassador.phoneHint'))}
          {field('notes', <textarea {...attributes('notes')} rows={4} maxLength={5000} value={notes} onChange={e => setNotes(e.target.value)} />, t('ambassador.notesHint'))}
          {admin && <div className={cn('border-t pt-5 space-y-5', borderPrimary)}>
            <h3 className={cn('font-semibold', primaryText)}>{t('ambassador.agreement')}</h3>
            {field('status', <select {...attributes('status')} value={status} onChange={e => { setStatus(e.target.value as ReferralStatus); setConfirmCorrection(false); }}>{REFERRAL_STATUSES.map(item => <option key={item} value={item}>{t(`ambassador.status.${item}`)}</option>)}</select>)}
            {converted && <>
              {field('convertedRestaurantCount', <input {...attributes('convertedRestaurantCount')} type="number" min={1} max={Number(restaurantCount)} step={1} required value={convertedCount} onChange={e => setConvertedCount(e.target.value)} />)}
              {field('monthlyPricePerRestaurantCents', <input {...attributes('monthlyPricePerRestaurantCents')} type="number" min="0.01" max="21474836.47" step="0.01" required value={price} onChange={e => setPrice(e.target.value)} />, t('ambassador.billingHint'))}
              {field('commissionStartDate', <input {...attributes('commissionStartDate')} type="date" required max={status === 'churned' && churnedOn ? churnedOn : today} value={startDate} onChange={e => setStartDate(e.target.value)} />, t('ambassador.startHint'))}
              {status === 'churned' && field('churnedOn', <input {...attributes('churnedOn')} type="date" required min={startDate || undefined} max={today} value={churnedOn} onChange={e => setChurnedOn(e.target.value)} />, t('ambassador.churnHint'))}
              <p className={cn('text-sm font-semibold', greenTextColor)}>{t('ambassador.commissionPreview', { amount: currencyFormatter.format(Number.isFinite(previewCents) ? previewCents / 100 : 0) })}</p>
              <p className={cn('text-xs', secondaryText)}>{t('ambassador.correctionHint')}</p>
              {lead?.status === 'churned' && status === 'won' && <p className={cn('text-sm', secondaryText)}>{t('ambassador.undoChurnHint')}</p>}
            </>}
            {needsConfirmation && <label className={cn('flex gap-3 text-sm', secondaryText)}>
              <input type="checkbox" className="mt-1" checked={confirmCorrection} onChange={e => setConfirmCorrection(e.target.checked)} required />
              {t('ambassador.resetConfirmation')}
            </label>}
          </div>}
        </form>
        {admin && lead && <div className={cn('border-t mt-5 pt-5 space-y-3', borderPrimary)}>
          {confirmDelete && <p role="alert" className={cn('text-sm', secondaryText)}>{t('ambassador.deleteWarning', { name: lead.businessName })}</p>}
          <div className="flex flex-wrap gap-3">
            <button type="button" disabled={saving || error?.code === 'stale_lead' || error?.code === 'not_found'} onClick={() => confirmDelete ? void remove() : setConfirmDelete(true)}
              className="min-h-[44px] px-4 py-2 rounded-lg text-sm font-semibold text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 disabled:opacity-50">
              {t(saving && confirmDelete ? 'ambassador.deleting' : confirmDelete ? 'ambassador.confirmDelete' : 'ambassador.deleteLead')}
            </button>
            {confirmDelete && <button type="button" disabled={saving} onClick={() => setConfirmDelete(false)} className={slideOverCancelClass}>{t('ambassador.cancelDelete')}</button>}
          </div>
        </div>}
      </InventorySlideOver>
    </div>
  );
}
